import React, { useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import styles from './OrderConfirmation.module.scss';
import classnames from 'classnames/bind';
import { Button } from '@components';
import { UseFormResult } from 'hooks';
import { OrderConfirmationFormFieldNames } from './form';
import { pushPurchaseEvent, UserContext } from '@utils';
import { Input } from 'gd-design-library';
import { ROUTES } from '@constants';
import { Cart } from '@services/cart';
import { OrderExpanded } from '@services/checkout';

const cx = classnames.bind(styles);

const CN = 'order-confirmation';
const confirmationMessageClassName = `${CN}__message`;
const createAccountClassName = `${CN}__create-account`;
const emailClassName = `${CN}__email`;

interface Props {
  email: string,
  formData: UseFormResult<OrderConfirmationFormFieldNames>,
  onCreateAccount: () => Promise<void>,
  isLoading: boolean,
  order: Cart | OrderExpanded,
  errorMessage?: string
  shouldShowSignInButton: boolean
}

export const OrderConfirmation: React.FC<Props> = ({
  email,
  formData: {
    state,
    errors,
    handleBlur,
    handleChange,
    isValid
  },
  onCreateAccount,
  isLoading,
  order,
  errorMessage,
  shouldShowSignInButton
}) => {
  const { user } = useContext(UserContext);
  const router = useRouter();

  useEffect(() => {
    pushPurchaseEvent(order);
  }, [ order ]);

  const handleContinueShopping = (): void => {
    router.push(ROUTES.catalog);
  };

  const handleViewOrderHistory = (): void => {
    router.push(ROUTES.myAccount);
  };

  return (
    <form autoComplete='off' className={ cx(CN) }>
      <div className={ cx(confirmationMessageClassName) }>
        { `${user ? `${user?.firstName}, t` : 'T'}hank you for your order` }
      </div>
      {!user && (
        <>
          <div className={ cx(createAccountClassName) }>
            Create Account
          </div>
          <div className={ cx(emailClassName) }>
            <Input
              disabled
              required
              name='email'
              variant='email'
              label='Email'
              value={ email }
              color={ errorMessage ? 'error' : undefined }
              helperText={ errorMessage || '' }
            />
            {shouldShowSignInButton && (
              <Button
                variant='inherit'
                onClick={ () => router.push(ROUTES.signIn) }
                style={ { alignSelf: 'flex-start' } }
              >
              Sign in instead
              </Button>
            )}
          </div>
          <Input
            required
            name='password'
            variant='password'
            label='Password'
            value={ state.password }
            onBlur={ handleBlur }
            onChange={ handleChange }
            color={ errors.password.isTouched && !errors.password.isValid ? 'error' : undefined }
            helperText={ errors.password.isTouched && !errors.password.isValid ? errors.password.message : '' }
          />
          <Button
            loading={ isLoading }
            onClick={ onCreateAccount }
            disabled={ !isValid }
          >
            Create Account
          </Button>
          <Button
            variant='outlined'
            onClick={ handleContinueShopping }
          >
            Continue Shopping
          </Button>
        </>
      )}
      {user && (
        <>
          <Button
            onClick={ handleContinueShopping }
          >
            Continue Shopping
          </Button>
          <Button
            variant='outlined'
            onClick={ handleViewOrderHistory }
          >
            View Order History
          </Button>
        </>
      )}
    </form>
  );
};

